"use client";

import { useRef, useEffect } from "react";
import { ChatMessage } from "./messages/ChatMessage";
import { WelcomeScreen } from "./messages/WelcomeScreen";
import { TypingIndicator } from "./messages/TypingIndicator";
import { Attachment } from "@/lib/api";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  attachments?: Attachment[];
}

interface ChatMessagesContainerProps {
  messages: Message[];
  isTyping: boolean;
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  chatId?: string;
  sessionId?: string;
  streamingMessageId?: string;
  awaitingStreamingRevealMessageId?: string;
  onStreamingMessageFirstReveal?: (messageId: string) => void;
}

/**
 * ChatMessagesContainer - The scrollable messages area
 * This container handles:
 * - Scrollable message list with proper overflow
 * - Auto-scroll to bottom on new messages
 * - Welcome screen when no messages
 * - Independent scrolling within fixed bounds
 */
export function ChatMessagesContainer({
  messages,
  isTyping,
  onSendMessage,
  disabled = false,
  chatId,
  sessionId,
  streamingMessageId,
  awaitingStreamingRevealMessageId,
  onStreamingMessageFirstReveal,
}: ChatMessagesContainerProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 min-h-0 overflow-y-auto bg-transparent scrollbar-chat"
      style={{
        // Use stable, well-supported properties
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch', // For iOS momentum scrolling
        // Simple height that works everywhere
        height: '100%',
      }}
    >
      <div className="mx-auto max-w-3xl px-4 lg:px-8">
        {/* Fixed padding values that work across all browsers */}
        <div className="pt-14 lg:pt-4">
          {messages.length === 0 ? (
            <WelcomeScreen onSendMessage={onSendMessage} disabled={disabled} />
          ) : (
            <div className="space-y-8">
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  chatId={chatId}
                  sessionId={sessionId}
                  isStreamingMessage={message.id === streamingMessageId}
                  onFirstStreamingReveal={
                    message.id === awaitingStreamingRevealMessageId
                      ? () => onStreamingMessageFirstReveal?.(message.id)
                      : undefined
                  }
                />
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
          {/* Simple bottom padding for input area */}
          <div className="h-32 lg:h-8" />
        </div>
      </div>
    </div>
  );
}
